import { retrieveRawLaunchParams } from "@tma.js/sdk-react";
import { IUserAlternativeTemplate } from "@/types/event-template-alternative-user";
import { BaseService } from "./BaseService";
import axiosInstance from "@/lib/axios";
import type { SaleManagementResponse } from "@/types/sale-management";
import { getSaleIdentifier } from "@/utils/saleRecord";
import { notifyAuthSessionChanged } from "@/lib/auth-session";
import { clearAuthenticatedSessions, replaceAuthenticatedSession, type AuthenticatedUser } from "@/lib/authenticated-session";
import { browserTelegramInitData, telegramAuthenticationHeaders, telegramActivationRequest } from "@/utils/telegramIdentity";

import type { VerifyTelegramActivationDto } from "@/types/telegram-activation";
export type { VerifyTelegramActivationDto } from "@/types/telegram-activation";

export interface ActivationResponse {
  accessToken: string;
  token?: string;
  refreshToken: string;
  user?: AuthenticatedUser;
  permissions?: string[];
  message?: string;
}

export interface StatusResponse extends ActivationResponse {
  eventTemplateAlternativeUser?: IUserAlternativeTemplate;
  sale?: SaleManagementResponse;
  /** Legacy response alias retained for older backends and internal callers. */
  task?: SaleManagementResponse;
  invitationMessage?: IUserAlternativeTemplate["invitationMessage"];
  seoMessage?: IUserAlternativeTemplate["seoMessage"];
}

function activationInitData() {
  let rawLaunchParams = "";
  try { rawLaunchParams = retrieveRawLaunchParams(); } catch { /* WebApp and stored launch data remain available. */ }
  return browserTelegramInitData(rawLaunchParams);
}

class AuthService extends BaseService {
  private verifications = new Map<string, Promise<StatusResponse>>();
  private requestVersion = 0;
  constructor() {
    super("/api/self/auth");
  }

  async activate(data: VerifyTelegramActivationDto): Promise<StatusResponse> {
    const initData = activationInitData();
    const request = telegramActivationRequest(data, initData, true);
    const version = ++this.requestVersion;
    const response = await axiosInstance.post<{ data: StatusResponse }>(
      `${this.endpoint}/activate-telegram-group`,
      request,
      { headers: telegramAuthenticationHeaders(initData, data.telegramChatId, true) },
    );
    if (version !== this.requestVersion) throw new Error("Authentication session changed. Please try again.");
    return this.acceptResponse(response.data.data, data.telegramChatId);
  }

  verifyActivation(data: VerifyTelegramActivationDto): Promise<StatusResponse> {
    const initData = activationInitData();
    const request = telegramActivationRequest(data, initData);
    const key = JSON.stringify([request.telegramChatId, request.telegramUserId, request.activationCode || ""]);
    const pending = this.verifications.get(key);
    if (pending) return pending;
    const version = ++this.requestVersion;
    const verification = axiosInstance.post<{ data: StatusResponse }>(
      `${this.endpoint}/verify-telegram-activation`, request,
      { headers: telegramAuthenticationHeaders(initData, data.telegramChatId, true) },
    ).then((response) => {
      if (version !== this.requestVersion) throw new Error("Authentication session changed. Please try again.");
      return this.acceptResponse(response.data.data, data.telegramChatId);
    })
      .finally(() => { if (this.verifications.get(key) === verification) this.verifications.delete(key); });
    this.verifications.set(key, verification);
    return verification;
  }

  private acceptResponse(result: StatusResponse, telegramChatId: number): StatusResponse {
    if (result) result = { ...result, accessToken: result.accessToken || result.token || "", task: result.sale || result.task };
    if (!result?.accessToken || !result.refreshToken) {
      throw new Error("Invalid activation response.");
    }
    if (!result.task && result.eventTemplateAlternativeUser) {
      result = { ...result, eventTemplateAlternativeUser: {
        ...result.eventTemplateAlternativeUser,
        ...(result.invitationMessage !== undefined ? { invitationMessage: result.invitationMessage } : {}),
        ...(result.seoMessage !== undefined ? { seoMessage: result.seoMessage } : {}),
      } };
    }
    this.saveInfo({ ...result, telegramChatId });
    return result;
  }

  async checkStatus(chatId: number, telegramUserId?: number) {
    return this.verifyActivation({ telegramChatId: chatId, telegramUserId });
  }

  async logout(): Promise<void> {
    this.requestVersion++;
    clearAuthenticatedSessions();
    this.verifications.clear();
    notifyAuthSessionChanged();
  }

  saveInfo(data: ActivationResponse & { telegramChatId?: number; task?: SaleManagementResponse }) {
    this.verifications.clear();
    replaceAuthenticatedSession({ ...data, authorization: data.task
      ? { kind: "group-summary", saleId: getSaleIdentifier(data.task) } : { kind: "invitation" } }, Boolean(data.task));
    localStorage.setItem(
      "telegramChatId",
      (data.telegramChatId || 0)?.toString(),
    );
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem("authToken");
  }
}

export const authService = new AuthService();
